import { PlayIcon } from '@heroicons/react/20/solid';
import { useEffect, useState } from 'react';
import useNavigation from '../common/navigation';
import AppCard from '../components/AppCard';
import ProductTourWrapper from '../components/ProductTourWrapper';
import Sidebar from '../components/Sidebar';
import UpgradeModal from '../components/UpgradeModal';
import { useFeatureGate } from '../context/FeatureGate/FeatureGateContext';
import { useProductTourContext } from '../context/ProductTourContext';
import { useUserContext } from '../context/UserProvider';
import RecordAudioButton from '../RecordAudioButton';

export default function HomePage() {
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const { name } = useUserContext();

  const { goToClientsPage, goToNotesPage, goToTemplatesPage } = useNavigation();
  const links = [
    { name: 'Your Clients', id: 'home-clients-link', navigation: goToClientsPage },
    { name: 'Your Meeting Notes', id: 'home-notes-link', navigation: goToNotesPage },
    { name: 'Your Meeting Templates', id: 'home-templates-link', navigation: goToTemplatesPage }
  ];

  const { featureGate } = useFeatureGate();
  const shouldShowUpgradeButton = featureGate && !featureGate.isPaidTier();

  // ============================== PRODUCT TOUR ==============================
  const { state: tourState, setState: setTourState } = useProductTourContext();

  const startTour = () => setTourState({ ...tourState, tourActive: true, run: true, stepIndex: 0 });
  const stopTour = () => setTourState({ ...tourState, tourActive: false, run: false });
  const pauseTour = () => setTourState({ ...tourState, run: false });
  const resumeTour = () => setTourState({ ...tourState, run: true });

  const restartTour = () => {
    stopTour();
    setTimeout(() => startTour(), 200);
  };

  useEffect(() => {
    // If tour is already running, pause and resume it so it renders correctly
    if (tourState.tourActive) {
      pauseTour();
      const timer = setTimeout(() => resumeTour(), 600); // ms delay to ensure the tour is active before setting the step index.
      return () => clearTimeout(timer);
    }
  }, []);
  // ============================== END PRODUCT TOUR ===========================

  return (
    <ProductTourWrapper>
      <Sidebar currentPageName={'Home'} />
      <main className="flex-1 lg:ml-72 h-screen relative">
        <div className="relative w-full">
          {/* Upgrade Button */}
          {shouldShowUpgradeButton && (
            <button
              onClick={() => setIsUpgradeModalOpen(true)}
              className="absolute flex items-center top-6 right-36 z-50 
                 hover:bg-teal-400 text-sm font-semibold text-white
                 px-3.5 py-2 rounded-lg shadow-lg bg-teal-500
                 transition duration-300"
            >
              <p>Upgrade</p>
            </button>
          )}

          {/* Original Tutorial Button */}
          <button
            onClick={restartTour}
            className="absolute flex items-center top-6 right-6 z-50 
                 hover:bg-indigo-400 text-sm font-semibold text-white
                 px-3.5 py-2 rounded-lg shadow-lg bg-indigo-600
                 transition duration-300"
          >
            <PlayIcon className="h-3.5 w-3.5 mr-2" />
            <p>Tutorial</p>
          </button>
        </div>
        {/*
        <div className="relative w-full">
          <button
            onClick={restartTour}
            className="absolute flex items-center top-6 right-6 z-50 hover:bg-indigo-400 text-sm font-semibold text-white px-3.5 py-2 rounded-lg shadow-lg bg-indigo-600 transition duration-300"
          >
            <PlayIcon className="h-3.5 w-3.5 mr-2" />
            <p>Tutorial</p>
          </button>
        </div>
        */}

        {/* Top Content */}
        <div className="flex w-full h-full items-center">
          <div className="max-w-7xl px-6 lg:px-8">
            <div className="-mt-32 max-w-2xl lg:mx-0">
              <h2 className="text-4xl font-semibold tracking-tight text-gray-700 sm:text-5xl">
                Hello, <span className="font-bold bg-indigo-50">{name}</span>
              </h2>
              <p className="mt-6 text-lg leading-8 text-gray-500">
                Simplify your workflow with AI-powered note automation. Build custom meeting
                templates, access client information, and streamline paperwork, so you can focus on
                what truly matters.
              </p>
            </div>
            <div id="linkContainer" className="mx-auto mt-10 max-w-2xl lg:mx-0 lg:max-w-none">
              <div className="grid grid-cols-1 gap-x-8 gap-y-6 text-base font-semibold leading-7 sm:grid-cols-2 md:flex lg:gap-x-10">
                {links.map((link) => (
                  <button
                    key={link.name}
                    id={link.id}
                    onClick={link.navigation}
                    className="rounded bg-indigo-50 hover:bg-indigo-500 px-6 py-1 text-indigo-800 hover:text-white transition-color duration-300"
                  >
                    {link.name} <span aria-hidden="true">&rarr;</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Row (positioned independently) */}
        <div className="absolute bottom-8 left-6 right-6 flex sm:items-center sm:justify-between md:justify-end lg:justify-end">
          <div className="w-72 md:mr-36 lg:mr-36">
            <AppCard />
          </div>
          <div>
            <RecordAudioButton />
          </div>
        </div>
      </main>
      <UpgradeModal isOpen={isUpgradeModalOpen} setIsOpen={setIsUpgradeModalOpen} />
    </ProductTourWrapper>
  );
}
